import type { TvRoomOffer, TvRoomOfferFeed } from "@shared/types/roomOffer";
import {
  slotActive,
  useScheme,
  type ResolvedOfferSlot,
  type ResolvedScheme,
} from "../schemeContext";

/**
 * Resolving a home-slot `offer` fill against the live catalogue (`POS-4`).
 *
 * Kept apart from the renderer, and pure, because everything that can go wrong
 * with this banner is a resolution question rather than a drawing one: a
 * deleted offer, an offer whose window closed, a scarcity claim that would be
 * stale. Those are the things that must be provably right, so they are tested
 * without a DOM (`offerSlot.test.ts`).
 *
 * ## Fail-soft is the whole contract
 *
 * A dangling, disabled, or out-of-window `offerId` resolves to `null`, and the
 * slot renders NOTHING - no placeholder, no error card, no reserved gap. The
 * home screen of a hotel is the last place in the product where a broken
 * reference may be allowed to look like a fault: the guest cannot tell a missing
 * promotion from a missing feature, and a template that shifted its layout
 * around an empty box would make the fault look deliberate.
 *
 * The feed has ALREADY dropped disabled and out-of-window offers server-side
 * (`POS-2`), so "is this offer live?" is answered here by one question - is it
 * in the feed - and never by a second copy of the availability rules.
 */

/**
 * Tokens an operator may write into their headline to have the REAL remaining
 * count substituted. Two spellings because the copy is hand-written in two
 * languages and `{kvar}` reads as noise in an English sentence; they mean
 * exactly the same thing.
 */
export const SCARCITY_TOKENS = ["{kvar}", "{left}"] as const;

/** Does this headline ask for the live count? */
export function wantsScarcityCount(headline: string): boolean {
  return SCARCITY_TOKENS.some((t) => headline.includes(t));
}

/**
 * Seats left on the offer's NEXT bookable hour, or `null` when the offer has no
 * seat ledger at all (a promotion, an order, an `asap` booking).
 *
 * The next hour rather than a sum over the horizon, deliberately. A spa evening
 * that runs for three days has forty-odd seats left in total, which is not a
 * scarcity claim anybody would make; the number the guest is about to act on -
 * and the first one POS-7's picker will draw - is the soonest hour they can
 * actually take. Feed order is chronological (`expandOfferSlots`), so the first
 * hour with room is the soonest one.
 *
 * `0` means every published hour is taken. That is a real answer and the banner
 * says so in words rather than interpolating a zero into the operator's
 * sentence.
 */
export function offerRemaining(offer: TvRoomOffer): number | null {
  if (offer.slots.length === 0) return null;
  const open = offer.slots.find((s) => s.remaining > 0);
  return open ? open.remaining : 0;
}

/**
 * Fill the operator's scarcity token with the live count.
 *
 * Returns `""` - drop the line - when the sentence asks for a number we cannot
 * honestly supply (`remaining` is `null`, i.e. this offer keeps no seat ledger,
 * or it is `0` and the sentence would read "Bara 0 platser kvar"). Printing the
 * raw `{kvar}` on a television, or a zero dressed as scarcity, is worse than
 * showing the offer's own words instead.
 */
export function fillScarcity(
  headline: string,
  remaining: number | null
): string {
  const text = headline.trim();
  if (!text) return "";
  if (!wantsScarcityCount(text)) return text;
  if (remaining == null || remaining <= 0) return "";
  let out = text;
  for (const token of SCARCITY_TOKENS)
    out = out.split(token).join(`${remaining}`);
  return singularise(out, remaining);
}

/**
 * Swedish and English plurals, for the one case the token creates.
 *
 * "Bara {kvar} platser kvar!" is written once, by an operator, for the common
 * case - and then the ledger reaches one seat and the television says "1
 * platser", which is exactly the sentence a guest reads twice and a colleague
 * screenshots. The count is generated, so the noun after it has to be too.
 *
 * Deliberately narrow: it fixes the words a scarcity line actually uses,
 * immediately after the number this function just wrote, and leaves every other
 * word alone. A general pluraliser for two languages is a library, and a wrong
 * guess on the operator's own copy is worse than the plural it corrects.
 */
const PLURALS: [RegExp, string][] = [
  [/\b1 platser\b/g, "1 plats"],
  [/\b1 tider\b/g, "1 tid"],
  [/\b1 rum\b/g, "1 rum"],
  [/\b1 places\b/g, "1 place"],
  [/\b1 seats\b/g, "1 seat"],
  [/\b1 spots\b/g, "1 spot"],
  [/\b1 times\b/g, "1 time"],
  [/\b1 left are\b/g, "1 left is"],
];

function singularise(text: string, remaining: number): string {
  if (remaining !== 1) return text;
  return PLURALS.reduce((out, [re, one]) => out.replace(re, one), text);
}

/** A home-slot offer fill, resolved against the live feed and ready to draw. */
export interface OfferSlotBanner {
  /** The catalogue row, exactly as the feed sent it. */
  offer: TvRoomOffer;
  /** Seats on the next bookable hour; `null` when the offer keeps no ledger. */
  remaining: number | null;
  /** Every published hour is taken. The banner says so instead of a number. */
  fullyBooked: boolean;
  /**
   * The operator's scarcity line with the count filled in, or `""` when they
   * wrote none (or wrote one we cannot fill truthfully). The renderer falls back
   * to the offer's own body - never to copy nobody wrote.
   */
  headline: string;
}

/**
 * Resolve a slot fill against a feed. `null` means "render nothing", and is the
 * answer for every failure mode: not an offer fill, no feed yet, no such offer,
 * an offer the server did not consider live, an empty id.
 */
export function resolveOfferBanner(
  fill: ResolvedOfferSlot | undefined,
  feed: TvRoomOfferFeed | undefined,
  now: Date
): OfferSlotBanner | null {
  if (!fill || fill.type !== "offer") return null;
  if (!slotActive(fill, now)) return null;
  const offer = feed?.offers.find((o) => o.id === fill.offerId);
  if (!offer) return null;
  const remaining = offerRemaining(offer);
  return {
    offer,
    remaining,
    fullyBooked: remaining === 0,
    headline: fillScarcity(fill.headline ?? "", remaining),
  };
}

/**
 * The live banner for one manifest slot, or `null`.
 *
 * Templates call this for TWO reasons and both matter: `SchemeSlot` calls it to
 * decide what to draw, and the home screen calls it to decide whether its focus
 * ring has an extra cell to visit. One resolver behind both is what stops a
 * template from offering a d-pad cell that draws nothing (or drawing a banner
 * the remote cannot reach).
 *
 * `now` is passed in rather than ticked here so the two callers on one screen
 * share the host's clock and cannot disagree about the active window by a frame.
 */
export function useOfferSlot(
  slotId: string,
  now: Date
): OfferSlotBanner | null {
  const scheme = useScheme();
  return offerSlotBanner(scheme, slotId, now);
}

/** Every fill's banner, aligned by index. See {@link offerSlotBanners}. */
export function useOfferSlots(
  slotId: string,
  now: Date
): (OfferSlotBanner | null)[] {
  const scheme = useScheme();
  return offerSlotBanners(scheme, slotId, now);
}

/**
 * The same answer from an already-resolved scheme, for callers that are not
 * inside a component (tests, and a template that already holds the scheme).
 *
 * The `showPromotions` gate lives here, not only in the renderer: it is the
 * switch that turns the home screen into a shop window, and a focus cell that
 * survived it would be an invisible target on a d-pad.
 */
export function offerSlotBanner(
  scheme: ResolvedScheme,
  slotId: string,
  now: Date
): OfferSlotBanner | null {
  return offerSlotBanners(scheme, slotId, now)[0] ?? null;
}

/**
 * One banner per fill in the slot, aligned by index.
 *
 * A slot may draw up to three cards side by side, and any of them may be an
 * offer, so the answer is a LIST positioned against the fills - `null` where a
 * fill is not an offer, or is an offer that has since been deleted, disabled or
 * fallen out of its window. Aligned rather than compacted, because the renderer
 * walks fills and needs to ask "is THIS one a live offer?".
 */
export function offerSlotBanners(
  scheme: ResolvedScheme,
  slotId: string,
  now: Date
): (OfferSlotBanner | null)[] {
  const { knobs, content } = scheme;
  if (knobs.has("showPromotions") && !knobs.toggle("showPromotions")) {
    return [];
  }
  return (content.slots[slotId] ?? []).map((fill) =>
    fill.type === "offer" ? resolveOfferBanner(fill, scheme.offers, now) : null
  );
}
